import React, { useEffect, useState } from 'react';
import { BrowserRouter, useHistory } from 'react-router-dom';
import enUS from 'antd/lib/locale/en_US';
import enGB from 'antd/lib/locale/en_GB';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { I18nextProvider } from 'react-i18next';
import i18n, { initI18n } from './localization/i18n';
import RouteSwitch from './routes/RouteSwitch';
import MainRoute from './routes';
import { RootState } from '@app/redux/reducers/state';
import constants from '@app/constants';
import config from '@app/utils/config';
import { ConfigProvider, Modal, Switch, theme, } from 'antd';
import { getThemeConfig } from "./theme/theme";
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import { getReplaceTerms } from './api/fileAreaService';
import PendoIdentify from '@app/components/PendoIdentify';
import '@progress/kendo-theme-default/dist/all.css';

const { defaultAlgorithm, compactAlgorithm } = theme;

const getLocale = (locale: string | undefined) => {
  switch (locale?.toLowerCase()) {
    case 'en-gb':
      return enGB;
    default:
      return enUS;
  }
};
const App: React.FC<{ authUrl: string }> = ({ authUrl }) => {
  const history = useHistory();
  const { userPreferences } = useSelector((state: RootState) => state.userManagement);
  const [isCompact, setIsCompact] = useState(false);
  const isAuthenticated = useSelector((rootState: RootState) => rootState.auth.isAuthenticated);

  useEffect(() => {
    moment.locale(userPreferences?.localizationCultureCode ?? 'en');
  }, [userPreferences?.localizationCultureCode]);

  const oktaAuth = new OktaAuth({
    issuer: authUrl,
    clientId: config.clientId,
    redirectUri: constants.redirectUri,
    postLogoutRedirectUri: constants.logoutUrl,
    scopes: constants.scopes,
    tokenManager: {
      autoRenew: true,
      secure: true,
      storageKey: constants.tokenKey,
    },
  });

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: any) => {
    history.replace(
      toRelativeUrl(originalUri || constants.postSignedInRedirect, window.location.origin)
    );
  };

  const initI18nWithReplaceTerms = async (isAuthenticated: boolean | null) => {
    try {
      if (isAuthenticated) {
        const response = await getReplaceTerms();
        if (response) {
          await initI18n(response.data);
        }
      }
    } catch (err) {
      console.error("i18n init failed", err);
    }
  };

  useEffect(() => {
    initI18nWithReplaceTerms(isAuthenticated);
  }, [isAuthenticated]);

  // Use Antd's ConfigProvider.config (Recommended)
  // Remove the Modal.confirm override since ConfigProvider.config handles it globally
  useEffect(() => {
    // Configure global modal theme
    ConfigProvider.config({
      theme: getThemeConfig(isCompact),
    });
  }, [isCompact]);
 
  return (
    <ConfigProvider
      locale={getLocale(userPreferences?.localizationCultureCode)}
      theme={getThemeConfig(isCompact)}
    >
      <I18nextProvider i18n={i18n}>
          <>
            <Switch
              checked={isCompact}
              onChange={setIsCompact}
              checkedChildren="Compact"
              unCheckedChildren="Standard"
              style={{ marginBottom: 16,position: "absolute",
                right: 0,
                margin: '16px'}}
            />
          <PendoIdentify />
          <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              <RouteSwitch routes={MainRoute} />
          </Security>
          </>
      </I18nextProvider>
    </ConfigProvider>
  );
};

const AppWithRouterAccess: React.FC<{ authUrl: string }> = ({ authUrl }) => (
  <BrowserRouter>
    <App authUrl={authUrl} />
  </BrowserRouter>
);

export default AppWithRouterAccess ;